/**
 * Kit materials at runtime: one MeshPhysicalMaterial per (kit material, finish), built from the
 * manifest entry and its Blender-derived maps. Finishes are parameter sets (roughness scale,
 * clearcoat, colour multiplier, normal strength), never different textures. The browser never
 * generates wood pixels; every map comes from the kit.
 */
import * as THREE from 'three';
import type { KitFinish, KitMaterialEntry } from './types';

export interface MaterialMaps {
  colour?: THREE.Texture;
  normal?: THREE.Texture;
  roughness?: THREE.Texture;
}

const srgb = (c: [number, number, number]) => new THREE.Color().setRGB(c[0], c[1], c[2], THREE.SRGBColorSpace);

/** Configure wrap, repeat and filtering of a kit map for its entry (repeat = 1 / textureScaleMetres). */
export function configureMap(t: THREE.Texture, entry: KitMaterialEntry, anisotropy: number): void {
  const [su, sv] = entry.textureScaleMetres ?? [1, 1];
  const mirror = entry.wrap === 'mirror';
  t.wrapS = mirror ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;
  t.wrapT = entry.wrap === 'repeat-u-clamp-v' ? THREE.ClampToEdgeWrapping : mirror ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;
  t.repeat.set(1 / su, entry.kind === 'ply-edge' ? 1 : 1 / sv);
  t.anisotropy = anisotropy;
  t.needsUpdate = true;
}

export function createKitMaterial(id: string, entry: KitMaterialEntry, maps: MaterialMaps, finish: KitFinish, finishId: string): THREE.MeshPhysicalMaterial {
  const m = new THREE.MeshPhysicalMaterial({ name: `${entry.name}.${finishId}` });
  m.userData.kitMaterial = id;
  m.userData.finish = finishId;
  switch (entry.kind) {
    case 'face':
    case 'end': {
      m.map = maps.colour ?? null;
      m.normalMap = maps.normal ?? null;
      m.roughnessMap = maps.roughness ?? null;
      m.color = srgb(finish.colourMultiplier);
      m.roughness = finish.roughnessScale;
      m.metalness = 0;
      m.normalScale.set(finish.normalScale, finish.normalScale);
      m.clearcoat = finish.clearcoat;
      m.clearcoatRoughness = finish.clearcoatRoughness;
      m.vertexColors = true;
      break;
    }
    case 'ply-edge': {
      m.map = maps.colour ?? null;
      m.color = srgb(finish.colourMultiplier);
      m.roughness = Math.min(1, finish.roughnessScale * 1.05);
      m.clearcoat = finish.clearcoat * 0.5;
      m.clearcoatRoughness = Math.min(1, finish.clearcoatRoughness + 0.1);
      m.vertexColors = true;
      break;
    }
    case 'metal':
    case 'constant':
    case 'slot': {
      m.color = srgb(entry.colourSrgb ?? [0.5, 0.5, 0.5]);
      m.metalness = entry.metalness ?? 0;
      m.roughness = entry.roughness ?? 0.6;
      if (entry.kind === 'metal') m.envMapIntensity = 1.15;
      if (id === 'slot.shadow') {
        // hairline decals: drawn on both sides and pulled forward so they never z-fight
        m.side = THREE.DoubleSide;
        m.polygonOffset = true;
        m.polygonOffsetFactor = -2;
        m.polygonOffsetUnits = -2;
        m.envMapIntensity = 0.2;
      }
      break;
    }
  }
  return m;
}

/** A copy drawn in front of coplanar board faces (lipping, housing caps). */
export function overlayVariant(m: THREE.MeshPhysicalMaterial): THREE.MeshPhysicalMaterial {
  const v = m.clone();
  v.name = `${m.name}.overlay`;
  v.polygonOffset = true;
  v.polygonOffsetFactor = -1;
  v.polygonOffsetUnits = -1;
  return v;
}
